import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useGetOrdersStatus } from "./hooks/useGetOrdersStatus";
import styled from "styled-components";
import StatusMaintenanceOrders from "./StatusMaintenanceOrders";
import { useGetUserDetails } from "../authentication/useGetUserDetails";
import WIOSSLoader from "../../interface/WIOSSLoader";

const OrdersContainer = styled.div`
  display: flex;
  width: 100%;
  height: 100%;
  padding: 1rem;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  padding-left: 1rem;
  padding-bottom: 1rem;
  margin-top: 0.4rem;
  overflow-x: auto;
`;

const NoContentContainer = styled.div`
  display: flex;
  width: 100%;
  height: 100%;
  align-items: center;
  justify-content: center;
`;

const StyledHeading = styled.h5`
  color: var(--color-grey-500);
  padding: 1rem;
`;

function getCurrentShift() {
  const now = new Date();
  const minutes = now.getHours() * 60 + now.getMinutes();
  if (minutes >= 345 && minutes < 795) return "Day Shift";
  if (minutes >= 795 && minutes < 1245) return "Evening Shift";
  return "Night Shift";
}

function isInNightShiftWindow(plannedDateStr, now) {
  const planned = new Date(plannedDateStr);
  const plannedHours = planned.getHours();
  const plannedMinutes = planned.getMinutes();
  const plannedTotalMinutes = plannedHours * 60 + plannedMinutes;
  const currentHours = now.getHours();
  const currentTotalMinutes = currentHours * 60 + now.getMinutes();

  if (currentTotalMinutes >= 1275) {
    const sameDate = planned.toDateString() === now.toDateString();
    const nextDay = new Date(now);
    nextDay.setDate(now.getDate() + 1);
    const isTomorrow = planned.toDateString() === nextDay.toDateString();
    return (
      (sameDate && plannedTotalMinutes >= 1275) ||
      (isTomorrow && plannedTotalMinutes > 0 && plannedTotalMinutes < 375)
    );
  }

  if (currentTotalMinutes > 0 && currentTotalMinutes < 375) {
    const sameDate = planned.toDateString() === now.toDateString();
    const prevDay = new Date(now);
    prevDay.setDate(now.getDate() - 1);
    const isYesterday = planned.toDateString() === prevDay.toDateString();
    return (
      (sameDate && plannedTotalMinutes > 0 && plannedTotalMinutes < 375) ||
      (isYesterday && plannedTotalMinutes >= 1275)
    );
  }
  return false;
}

function isToday(dateStr) {
  const d = new Date(dateStr);
  const today = new Date();
  return d.toDateString() === today.toDateString();
}

function StatusMaintenance() {
  const { startDate, endDate } = useMemo(() => {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - 1);
    return { startDate: start, endDate: end };
  }, []);

  const { data } = useGetOrdersStatus(startDate, endDate);
  const [currentShift, setCurrentShift] = useState(getCurrentShift());
  const userImageRef = useRef({});
  const { mutate: fetchUserDetails } = useGetUserDetails();

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentShift(getCurrentShift());
    }, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  function isInPreMidnightNightShift(date) {
    const d = new Date(date);
    const now = new Date();

    const shiftStart = new Date(now);
    shiftStart.setHours(20, 0, 0, 0); // Today at 20:00

    const shiftEnd = new Date(now);
    shiftEnd.setDate(shiftEnd.getDate() + 1); // Tomorrow
    shiftEnd.setHours(6, 30, 0, 0); // Tomorrow at 06:30

    return d >= shiftStart && d < shiftEnd;
  }

  function isInPostMidnightNightShift(date) {
    const d = new Date(date);
    const now = new Date();

    const shiftStart = new Date(now);
    shiftStart.setDate(shiftStart.getDate() - 1); // Yesterday
    shiftStart.setHours(20, 0, 0, 0); // Yesterday at 20:00

    const shiftEnd = new Date(now);
    shiftEnd.setHours(6, 30, 0, 0); // Today at 06:30

    return d >= shiftStart && d < shiftEnd;
  }

  const filteredOrders = useMemo(() => {
    if (!data?.data) return [];

    const now = new Date();
    const currentShiftLower = currentShift.toLowerCase();

    return data.data.filter((order) => {
      const assigned = order.assignedShifts?.toLowerCase() || "";
      const planned = new Date(order.plannedDate);

      if (currentShift === "Night Shift" && assigned.includes("night shift")) {
        return (
          isInPreMidnightNightShift(planned) ||
          isInPostMidnightNightShift(planned)
        );
      }

      return (
        assigned.includes(currentShiftLower) &&
        planned.toDateString() === now.toDateString()
      );
    });
  }, [data, currentShift]);

  const fetchUserImage = useCallback(
    (name) =>
      new Promise((resolve) => {
        fetchUserDetails(
          { wiToolName: name },
          {
            onSuccess: (response) => {
              const img = response?.data?.data?.userImageBase64;
              if (response.userExists && img && img.trim() !== "") {
                userImageRef.current[name] = img;
                // console.log("✅ Stored", name);
              } else {
                //console.log("⚠️ No image for", name);
              }
              resolve();
            },
            onError: () => {
              //console.log("❌ Fetch error for", name);
              resolve();
            },
          }
        );
      }),
    [fetchUserDetails]
  );

  useEffect(() => {
    async function preloadImages() {
      const uniqueNames = new Set();
      filteredOrders.forEach((order) => {
        order.assignedResources
          ?.split("•")
          .map((n) => n.trim())
          .filter(Boolean)
          .forEach((name) => {
            if (!(name in userImageRef.current)) {
              uniqueNames.add(name);
            }
          });
      });

      //console.log("🟡 Unique names to fetch:", [...uniqueNames]);

      for (const name of uniqueNames) {
        await fetchUserImage(name);
      }

      // console.log("🧠 Final userImageRef:", { ...userImageRef.current });
    }

    if (filteredOrders.length) preloadImages();
  }, [filteredOrders, fetchUserImage]);

  // filteredOrders && console.log("orders to render", filteredOrders);
  return (
    <OrdersContainer>
      <StyledHeading>Maintenance Orders - {currentShift}</StyledHeading>
      {filteredOrders.length > 0 ? (
        filteredOrders.map((order) => (
          <StatusMaintenanceOrders
            key={order.id}
            item={order}
            userImages={userImageRef.current}
          />
        ))
      ) : (
        <NoContentContainer>
          <WIOSSLoader text="NO ORDERS FOR SHIFT" />
        </NoContentContainer>
      )}
    </OrdersContainer>
  );
}

export default StatusMaintenance;
